import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X, Globe } from "lucide-react";
import { useLang } from "../i18n";

export default function Navbar({ content }) {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "work", label: t.nav.work },
    { id: "reviews", label: t.nav.reviews },
    { id: "quote", label: t.nav.quote },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <button onClick={() => go("top")} className="flex items-center gap-3" data-testid="nav-logo">
          <img src={content.logo_url} alt="JJ Truck Repair" className="h-11 w-auto object-contain" />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="font-body font-semibold uppercase tracking-wider text-sm text-neutral-300 hover:text-white transition-colors relative group"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-primary-red transition-all duration-200 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="flex items-center gap-1.5 px-3 py-2 border border-white/15 text-white font-mono2 text-xs uppercase hover:border-primary-red hover:text-primary-red transition-colors"
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            <Globe size={14} />
            {lang === "en" ? "ES" : "EN"}
          </button>

          <a
            href={`tel:${content.phone.replace(/[^0-9]/g, "")}`}
            className="hidden sm:flex items-center gap-2 bg-primary-red text-white font-body font-bold uppercase text-sm px-5 py-2.5 hover:bg-[#FF1E16] transition-colors"
            data-testid="nav-call"
          >
            <Phone size={16} />
            {content.phone}
          </a>

          <button className="lg:hidden text-white" onClick={() => setOpen((o) => !o)} data-testid="nav-menu-toggle" aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-5"
          data-testid="mobile-menu"
        >
          {links.map((l) => (
            <button key={l.id} onClick={() => go(l.id)} className="text-left font-display text-2xl uppercase text-white" data-testid={`mobile-nav-${l.id}`}>
              {l.label}
            </button>
          ))}
          <a href={`tel:${content.phone.replace(/[^0-9]/g, "")}`} className="bg-primary-red text-white font-bold uppercase px-5 py-3 text-center mt-2">
            {content.phone}
          </a>
        </motion.div>
      )}
    </motion.header>
  );
}
